/* Moving the chart, from a panel that has never heard of MapLibre.
 *
 * `Chart.svelte` holds the map instance and always will — but search results
 * and saved trips both need the camera, and threading a `Map` through the panel
 * registry would put a MapLibre type in every panel's props for the sake of two
 * of them.
 *
 * One module, one registration, and the panels ask by name. The same seam
 * `configureSources` and `configurePassage` already use, applied to the camera
 * instead of to data.
 */
import type { Map as MlMap } from 'maplibre-gl';

let map: MlMap | null = null;

/** Called once, by the chart, when the map is ready. */
export function setMap(m: MlMap | null): void {
  map = m;
}

/** Centre on a point, keeping the current zoom unless it is very wide.
 *
 *  Zooming IN on a search result rather than jumping to a fixed zoom: somebody
 *  looking at a whole strait who searches a pass wants to see the pass, and
 *  somebody already at harbour scale does not want the chart to pull back. */
export function flyTo(lat: number, lon: number, minZoom = 12): void {
  if (!map) return;
  const z = Math.max(map.getZoom(), minZoom);
  map.easeTo({ center: [lon, lat], zoom: z, duration: 600 });
}

/** Frame a whole route.
 *
 *  Padded generously on every side: the panel column covers the right of the
 *  chart on a desktop and the sheet covers the bottom on a phone, and a route
 *  fitted edge-to-edge disappears under whichever one is open. */
export function fitTo(points: readonly { lat: number; lon: number }[]): void {
  if (!map || points.length === 0) return;
  let w = points[0]!.lon;
  let e = w;
  let s = points[0]!.lat;
  let n = s;
  for (const p of points) {
    if (p.lon < w) w = p.lon;
    if (p.lon > e) e = p.lon;
    if (p.lat < s) s = p.lat;
    if (p.lat > n) n = p.lat;
  }
  map.fitBounds(
    [
      [w, s],
      [e, n]
    ],
    { padding: 80, duration: 700, maxZoom: 14 }
  );
}
